import { debounce } from '@mui/material/utils';
import { UIEvent, useCallback, useEffect, useMemo } from 'react';

const DEFAULT_THRESHOLD_PX = 400;
const DEFAULT_FETCH_MORE_LIMIT = 50;

type OffsetBasedLoadMore = (localLimit?: number, localOffset?: number) => Promise<void> | null;

export const useScrollPaginateHandler = (params: {
  loadMore?: OffsetBasedLoadMore;
  enabled?: boolean;
  thresholdPx?: number;
  limit?: number;
  // It will be used to check top scroll instead of bottom scroll
  reversed?: boolean;
}) => {
  const {
    loadMore,
    enabled = false,
    thresholdPx = DEFAULT_THRESHOLD_PX,
    limit = DEFAULT_FETCH_MORE_LIMIT,
    reversed = false,
  } = params;

  const rawHandler = useCallback(
    async (e: UIEvent) => {
      if (!enabled) return;

      const el = e.target as HTMLElement;
      const scrollTop = el.scrollTop;
      const clientHeight = el.clientHeight;
      const scrollHeight = el.scrollHeight;

      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
      const distanceToTop = scrollTop;

      const isBottom = distanceToBottom <= thresholdPx;
      const isTop = distanceToTop <= thresholdPx;

      if (!reversed && isBottom) {
        await loadMore?.(limit);
      } else if (reversed && isTop) {
        await loadMore?.(limit);
      }
    },
    [enabled, thresholdPx, reversed, loadMore, limit],
  );

  const debouncedOnScroll = useMemo(() => {
    return debounce(rawHandler, 200);
  }, [rawHandler]);

  useEffect(() => {
    return () => {
      debouncedOnScroll.clear?.();
    };
  }, [debouncedOnScroll]);

  return debouncedOnScroll;
};
